
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout/Layout";
import { BlogPostEditor } from "@/components/blog/BlogPostEditor";
import { BlogPostsList } from "@/components/blog/BlogPostsList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, ListFilter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function BlogAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState("posts");
  const postsListRef = useRef<{ fetchPosts: () => Promise<void> }>(null);

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error(t('blog.admin.unauthorized'));
        navigate('/login');
        return;
      }
      
      // Check if the user is an admin
      try {
        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('username, role')
          .eq('id', session.user.id)
          .single();
        
        if (userError) throw userError;
        
        // Authorize if the user has the admin role or is the special admin user
        if (userData && (userData.role === 'admin' || userData.username === 'admin' || userData.username === 'andrew')) {
          setIsAuthorized(true);
        } else {
          toast.error(t('blog.admin.accessDenied'));
          navigate('/app');
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        toast.error(t('blog.admin.accessDenied'));
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate, t]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handlePostSaved = () => {
    // Switch to the posts tab to see the newly created post
    setActiveTab("posts");
    
    // Force a refresh of the posts list
    if (postsListRef.current && typeof postsListRef.current.fetchPosts === 'function') {
      postsListRef.current.fetchPosts();
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-12 text-center">
          <p>{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (!isAuthorized) {
    return (
      <Layout>
        <div className="container py-12 text-center">
          <p>{t('blog.admin.accessDenied')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-7xl py-6 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="bg-gradient-to-r from-primary to-green bg-clip-text text-transparent text-3xl font-bold">
              {t('blog.admin.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('blog.admin.description')}
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="posts">
              <ListFilter className="h-4 w-4 mr-2" />
              {t('blog.admin.managePosts')}
            </TabsTrigger>
            <TabsTrigger value="create">
              <PlusCircle className="h-4 w-4 mr-2" />
              {t('blog.admin.createPost')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="posts" className="space-y-4">
            <BlogPostsList ref={postsListRef} />
          </TabsContent>
          
          <TabsContent value="create">
            <BlogPostEditor onPostSaved={handlePostSaved} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
